/**
 * ClaimGift.js
 *
 * Gift claiming page where recipients provide payment method details.
 * Creates/updates recipient user profile and initiates payout process.
 * Tracks receiving history and updates user profiles in real-time.
 *
 * Route: /claim/:claimToken
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import StripeSetupInfo from './components/StripeSetupInfo';
import { SERVER_URL } from './config';

function ClaimGift() {
  const { claimToken } = useParams();
  const { user, signup, isAuthenticated } = useAuth();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [needsAccount, setNeedsAccount] = useState(false);

  useEffect(() => {
    // Fetch gift info using claim token
    fetch(`${SERVER_URL}/claim/${claimToken}`)
      .then(res => {
        if (!res.ok) throw new Error('Invalid or expired claim link');
        return res.json();
      })
      .then(data => {
        setGift(data);

        // If gift is already claimed, show the "already claimed" message immediately
        if (data.claimed) {
          setClaimed(true);
          setLoading(false);
          return; // Stop here, don't check for account needs
        }

        // Check if user needs to create an account
        // Only set needsAccount to true if user is definitely not authenticated
        // Wait for auth to load before deciding
        if (isAuthenticated === false && !user) {
          setNeedsAccount(true);
        } else if (isAuthenticated && user) {
          setNeedsAccount(false);
        }

        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [claimToken, isAuthenticated, user]);

  const handleClaim = async e => {
    e.preventDefault();
    setClaiming(true);
    setError('');

    try {
      // If user needs an account, create it first
      if (needsAccount) {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }

        // Create account using recipient's name and email from the gift
        await signup(gift.recipient_name, gift.recipient_email, password);
      }

      const res = await fetch(`${SERVER_URL}/claim/${claimToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_method: 'stripe_connect',
          payment_details: gift.recipient_email,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to claim gift');
      }

      const data = await res.json();

      // Handle Stripe Connect onboarding redirect
      if (data.requires_onboarding && data.onboarding_url) {
        // Store gift info for return
        localStorage.setItem(
          'claiming_gift',
          JSON.stringify({
            claimToken,
            amount: gift.amount,
            giver_name: gift.giver_name || 'Someone',
          })
        );

        // Redirect to Stripe onboarding
        window.location.href = data.onboarding_url;
        return;
      }

      // Handle successful claim
      if (data.success) {
        setClaimed(true);
        // Store the payout status and details for display
        setGift(prev => ({
          ...prev,
          payout_status: data.status,
          payout_message: data.message,
          next_step: data.next_step,
        }));
      } else {
        throw new Error(data.message || 'Failed to claim gift');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-md mx-auto mt-10 text-center">
        <p>Loading your gift...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto mt-10">
        <div className="bg-red-100 border border-red-400 text-red-700 p-4 rounded">
          <h2 className="font-bold mb-2">Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (claimed) {
    return (
      <div className="max-w-md mx-auto mt-10">
        <div className="bg-indigo-50 border border-indigo-300 text-indigo-900 p-6 rounded-lg">
          <h1 className="text-2xl font-bold mb-4">✅ Gift Already Claimed</h1>
          <p className="mb-4">
            This gift of <strong>${gift.amount}</strong> has already been claimed and set up for
            payout.
          </p>
          <div className="mt-4 p-4 bg-white rounded border border-indigo-200">
            <p className="text-sm text-gray-700">
              <strong>💡 What this means:</strong>
            </p>
            <ul className="list-disc list-inside text-sm text-gray-700 mt-2 space-y-1">
              <li>Your Stripe account has been set up</li>
              <li>Funds will be transferred to your bank account automatically</li>
              <li>Money typically arrives in 1-2 business days</li>
              <li>You'll receive a confirmation email when the transfer completes</li>
            </ul>
          </div>
          <div className="mt-4 p-3 bg-indigo-100 rounded border border-indigo-300">
            <p className="text-xs text-indigo-800">
              <strong>Note:</strong> You don't need to do anything else. The payout process is
              already in progress.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-10">
      <div className="bg-indigo-50 border border-indigo-200 p-6 rounded-lg mb-6">
        <h1 className="text-2xl font-bold mb-4">💝 You've Received a Gift!</h1>
        <div className="space-y-2">
          <p>
            <strong>Amount:</strong> ${gift.amount}
          </p>
          <p>
            <strong>Occasion:</strong> {gift.occasion}
          </p>
          {gift.message && (
            <div className="mt-3 p-3 bg-white rounded border border-indigo-200">
              <p className="text-sm text-gray-600">Message:</p>
              <p className="italic">"{gift.message}"</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 p-6 rounded-lg">
        <h2 className="text-xl font-bold mb-4">Claim Your Gift</h2>

        {needsAccount && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-indigo-800 font-medium mb-2">
              🔐 Step 1: Create Your Gifted Account
            </p>
            <p className="text-xs text-indigo-700 mb-3">
              Set a password to create your account on Gifted (this platform). Next, you'll set up
              Stripe to receive the money.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Create Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="••••••••"
                  required={needsAccount}
                />
                <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="••••••••"
                  required={needsAccount}
                />
              </div>
            </div>
          </div>
        )}

        <StripeSetupInfo showStepNumber={needsAccount} showDetailedInfo={true} />

        <form onSubmit={handleClaim} className="space-y-4">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 p-3 rounded text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={claiming || (needsAccount && (!password || !confirmPassword))}
            className="w-full bg-indigo-500 text-white p-3 rounded font-medium hover:bg-indigo-600 disabled:bg-gray-400 transition-colors"
          >
            {claiming
              ? 'Processing...'
              : needsAccount
                ? `Create Account & Claim $${gift.amount}`
                : `Set Up Account & Claim $${gift.amount}`}
          </button>

          <p className="text-xs text-gray-500 text-center">
            You'll be redirected to Stripe to securely set up your account and add your bank
            details.
          </p>
        </form>
      </div>
    </div>
  );
}

export default ClaimGift;
